import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import Comm from "../../Comm"
import DateRangePickerWithPresets from "../../components/DateRangePickerWithPresets"
import LoaderDots from "../../components/LoaderDots"
import { BREAKPOINTS } from "../../GlobalTheme"
import Icon from "../../Icon"
import ChargerType from "../../types/ChargerType"

// Daily MID and internal meter readings with the daily consumption, which replaces the daily sheet of
// the report Excel (VLT-2879). The backend (VLT-2878) does the day splitting, so a charge running past
// midnight or a month end is already divided by the time it gets here.

type MeterStatus = 'OK' | 'NO_RECORD' | 'NO_BASELINE' | 'METER_ERROR' | 'POWER_OFF' | 'METER_REPLACED'

interface MeterDay {
  opening: number | null
  closing: number | null
  consumption: number | null
  status: MeterStatus
}

interface DailyMeterResponse {
  chargerId: string
  from: string
  to: string
  branches: ('MID' | 'INTERNAL')[]
  days: { day: string, mid?: MeterDay | null, internal?: MeterDay | null }[]
  totals: { mid?: number | null, internal?: number | null }
}

const MAX_DAYS = 366

const STATUS_KEYS: { [status: string]: string } = {
  NO_RECORD: 'charger_noData',
  NO_BASELINE: 'charger_noPreviousReading',
  METER_ERROR: 'charger_meterError',
  POWER_OFF: 'charger_powerOutage',
  METER_REPLACED: 'charger_meterReplaced',
}

const Container = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: 18px;
  border-radius: 24px;
`

// Opens and closes like the Device information card above it, and starts closed.
const Header = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

const Title = styled.p`
  font-weight: 600;
  font-size: 18px;
  color: ${props => props.theme.colors.gray_900};
`

const ArrowContainer = styled.div`
  margin-left: auto;
  padding-right: 12px;
`

const PickerRow = styled.div`
  margin-bottom: 12px;
`

// Scrolls inside the card on a wide screen, where the page itself does not. On a phone the page
// scrolls, and a second vertical scroller in it would trap the gesture, so only the width scrolls.
const TableScroller = styled.div`
  overflow: auto;
  max-height: 440px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    max-height: none;
  }
`

const MeterTable = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;

  th, td {
    padding: 10px 12px;
    text-align: right;
    white-space: nowrap;
  }

  th:first-child, td:first-child {
    text-align: left;
  }

  /* The lines under the header and above the total are shadows: with collapsed borders a border
     stays with the table while the sticky cell moves, so it would scroll away. */
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: 12px;
    font-weight: 600;
    color: ${props => props.theme.colors.gray_500};
    background-color: ${props => props.theme.colors.white};
    box-shadow: inset 0px -1px 0px ${props => props.theme.colors.gray_200};
  }

  td {
    font-size: 14px;
    font-weight: 600;
    color: ${props => props.theme.colors.gray_900};
    border-bottom: 1px solid ${props => props.theme.colors.gray_100};
  }

  tfoot td {
    position: sticky;
    bottom: 0;
    font-weight: 700;
    background-color: ${props => props.theme.colors.white};
    box-shadow: inset 0px 1px 0px ${props => props.theme.colors.gray_200};
    border-bottom: none;
  }
`

const StatusText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.gray_500};
`

const Message = styled.p`
  font-size: 14px;
  line-height: 1.5;
  margin: 8px 0px 0px;
  color: ${props => props.theme.colors.gray_500};
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0px;
`

const pad = (n: number) => String(n).padStart(2, '0')
const toDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const formatKwh = (value?: number | null) => value == null ? '-' : value.toFixed(2)

// This month so far, up to yesterday: today has no closing reading yet. On the 1st that is the whole
// previous month, which is the period a monthly bill needs.
function defaultRange(): Date[] {
  const now = new Date()
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  return [
    new Date(yesterday.getFullYear(), yesterday.getMonth(), 1),
    new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999),
  ]
}

export default function DailyMeterReadings({ charger }: { charger: ChargerType }) {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState<boolean>(false)
  // The chosen days, kept here because the picker unmounts while the card is closed and would come back
  // on its default otherwise. State also hands it the same array on every render; a new one would
  // reset its label.
  const [range, setRange] = useState<Date[]>(defaultRange)
  const [readings, setReadings] = useState<DailyMeterResponse | null>()
  const loadedKey = useRef<string | null>(null)

  const from = toDay(range[0])
  const to = toDay(range[1])
  // Days are counted on UTC midnights, so a daylight saving change cannot shift the count.
  const dayCount = Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1
  const isPeriodTooLong = dayCount > MAX_DAYS

  // Nothing is requested until the card is opened, and reopening it does not ask again for the
  // period already on screen.
  useEffect(() => {
    const key = `${charger.chargerId}/${from}/${to}`
    if (!isOpen || isPeriodTooLong || loadedKey.current === key) return
    let isCurrent = true
    setReadings(undefined)
    Comm(`/api-charger/charger/dailymeter?from=${from}&to=${to}`, { headers: { charger_id: charger.chargerId } })
      .then(async response => {
        if (!response.ok) throw new Error(`dailymeter answered ${response.status}`)
        const result: DailyMeterResponse = await response.json()
        if (!isCurrent) return
        loadedKey.current = key
        setReadings(result)
      })
      .catch(err => {
        console.log('Unable to load the daily meter readings', err)
        if (isCurrent) setReadings(null)
      })
    // An answer for a period the user has already left, or for a card closed meanwhile, is dropped.
    return () => { isCurrent = false }
  }, [isOpen, charger.chargerId, from, to, isPeriodTooLong])

  // The picker hands the start over a day late (see its emit()), so it is stepped back here.
  function handleSelect(selected: Date[]) {
    const start = new Date(selected[0].getFullYear(), selected[0].getMonth(), selected[0].getDate() - 1)
    const end = selected[1] ?? start
    setRange([start, new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999)])
  }

  const today = toDay(new Date())
  const branches = readings?.branches ?? ['MID', 'INTERNAL']
  const showMid = branches.includes('MID')
  const showInternal = branches.includes('INTERNAL')

  // Today has no closing reading until midnight, which is not the same as a day that never arrived.
  const isOpenToday = (day: string, meter?: MeterDay | null) => day === today && meter?.status === 'NO_RECORD'

  function meterCells(day: string, meter?: MeterDay | null) {
    const status = meter?.status
    const statusLabel = !status ? '-'
      : isOpenToday(day, meter) ? t('charger_notClosedYet')
      : STATUS_KEYS[status] ? t(STATUS_KEYS[status]) : status
    return <>
      <td>{formatKwh(meter?.closing)}</td>
      <td>{status === 'OK' ? formatKwh(meter?.consumption) : <StatusText>{statusLabel}</StatusText>}</td>
    </>
  }

  return (
    <Container>
      <Header role="button" aria-expanded={isOpen} onClick={() => setIsOpen(!isOpen)}>
        <Title>{t('charger_dailyMeterReadings')}</Title>
        <ArrowContainer>
          <Icon path={isOpen ? "general.upArrow" : "general.downArrow"} width="22px" height="22px" color="gray_600" />
        </ArrowContainer>
      </Header>
      {isOpen && <>
      <PickerRow>
        <DateRangePickerWithPresets onSelect={handleSelect} defaultDate={range} />
      </PickerRow>
      {isPeriodTooLong ? (
        <Message>{t('charger_selectAPeriodOfAtMost366Days')}</Message>
      ) : readings === undefined ? (
        <LoaderContainer><LoaderDots /></LoaderContainer>
      ) : readings === null ? (
        <Message>{t('charger_couldNotLoadTheDailyMeterReadings')}</Message>
      ) : <>
        <TableScroller>
          <MeterTable>
            <thead>
              <tr>
                <th>{t('charger_date')}</th>
                {showMid && <>
                  <th>{`${t('charger_midMeterReading')} (kWh)`}</th>
                  <th>{`${t('charger_midConsumption')} (kWh)`}</th>
                </>}
                {showInternal && <>
                  <th>{`${t('charger_internalMeterReading')} (kWh)`}</th>
                  <th>{`${t('charger_internalConsumption')} (kWh)`}</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {readings.days.map(({ day, mid, internal }) => (
                <tr key={day}>
                  <td>{day.replace(/-/g, '.')}</td>
                  {showMid && meterCells(day, mid)}
                  {showInternal && meterCells(day, internal)}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>{t('charger_total')}</td>
                {showMid && <><td /><td>{formatKwh(readings.totals?.mid)}</td></>}
                {showInternal && <><td /><td>{formatKwh(readings.totals?.internal)}</td></>}
              </tr>
            </tfoot>
          </MeterTable>
        </TableScroller>
      </>}
      </>}
    </Container>
  )
}
